// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module FunnelAnalysisPage
 * @package @geenius/analytics/react-css
 * @description Page-level React CSS composition for configuring and reviewing
 * analytics funnel analysis.
 */

import {
  type AnalyticsEvent,
  analyzeFunnel,
  buildAnalyticsSeoMeta,
  type FunnelResult,
  type FunnelStep,
} from "@geenius/analytics";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Skeleton,
} from "@geenius/ui/react-css";
import type React from "react";
import { useMemo, useState } from "react";
import { FunnelChart } from "../components/FunnelChart";
import { AnalyticsPageErrorBoundary } from "../components/AnalyticsPageErrorBoundary";
import {
  type AnalyticsErrorStateValue,
  ErrorState,
} from "../components/ErrorState";
import { StatsCard } from "../components/StatsCard";
import { useAnalyticsCopy } from "../lib/analyticsCopy";
import { cx } from "../lib/cx";
import { FadeIn } from "../lib/FadeIn";
import type { AnalyticsSeoMeta } from "./AnalyticsDashboardPage";

/**
 * Props for rendering the React CSS funnel analysis page.
 *
 * @property name Human-readable funnel name used when analyzing raw events.
 * @property steps Ordered funnel step configuration shown in the builder.
 * @property events Optional raw events used by the built-in analyze action.
 * @property result Optional precomputed funnel result to visualize.
 * @property onAnalyze Called after the built-in analyze action computes a result.
 * @property isLoading Whether the analysis surface should show loading state.
 * @property error Optional page-level error surfaced before funnel data sections.
 * @property onRetry Optional recovery callback shown in the page-level error state.
 * @property renderSeoMeta Optional renderer for host apps that mount SEO metadata.
 * @property className Optional class name merged into the page wrapper.
 */
export interface FunnelAnalysisPageProps {
  name?: string;
  steps?: FunnelStep[];
  events?: AnalyticsEvent[];
  result?: FunnelResult | null;
  onAnalyze?: (result: FunnelResult) => void;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  renderSeoMeta?: (meta: AnalyticsSeoMeta) => React.ReactNode;
  className?: string;
}

function formatPercent(value: number | undefined): string {
  return `${(value ?? 0).toFixed(1)}%`;
}

/**
 * Renders a page-level funnel builder and conversion analysis surface.
 *
 * @param props Funnel configuration, raw events, and optional precomputed result.
 * @returns A React CSS page composition for funnel analysis workflows.
 */
export function FunnelAnalysisPage({
  name,
  steps = [],
  events = [],
  result,
  onAnalyze,
  isLoading,
  error,
  onRetry,
  renderSeoMeta,
  className,
}: FunnelAnalysisPageProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const funnelName =
    name ?? copy.fallback("analytics.funnel.defaultName", "Conversion funnel");
  const [computedResult, setComputedResult] = useState<FunnelResult | null>(
    null,
  );
  const activeResult = result ?? computedResult;
  const canAnalyze = events.length > 0 && steps.length > 0;
  const seoMeta = buildAnalyticsSeoMeta({
    title: copy.fallback("analytics.seo.funnelTitle", "Funnel Analysis"),
    description: copy.fallback(
      "analytics.seo.funnelDescription",
      "Configure conversion steps and inspect funnel completion across analytics events.",
    ),
    path: "/analytics/funnels",
    keywords: ["analytics", "funnels", "conversion", "events"],
  });
  const stepRows = useMemo(
    () =>
      steps.map((step, index) => ({
        ...step,
        order: index + 1,
        propertyCount: Object.keys(step.properties ?? {}).length,
      })),
    [steps],
  );

  const runAnalysis = (): void => {
    const nextResult = analyzeFunnel(events, steps, funnelName);
    setComputedResult(nextResult);
    onAnalyze?.(nextResult);
  };

  return (
    <>
      {renderSeoMeta ? renderSeoMeta(seoMeta) : null}
      <AnalyticsPageErrorBoundary onRetry={onRetry}>
        <div
          className={cx(
            "gn-analytics-page",
            "gn-analytics-funnel-page",
            className,
          )}
          data-analytics-page="funnel-analysis"
        >
          <FadeIn>
            <header
              className="gn-analytics-page__header"
              data-analytics-page-header
            >
              <h1 className="gn-analytics-page__title">
                {copy.fallback("analytics.funnel.pageTitle", "Funnel Analysis")}
              </h1>
              <p className="gn-analytics-page__subtitle">
                {copy.fallback(
                  "analytics.funnel.pageSubtitle",
                  "Build ordered conversion steps and review drop-off across the journey.",
                )}
              </p>
            </header>
          </FadeIn>

          {error ? (
            <FadeIn delay={60}>
              <Card data-analytics-page-error>
                <CardContent>
                  <ErrorState error={error} onRetry={onRetry} />
                </CardContent>
              </Card>
            </FadeIn>
          ) : (
            <>
              <FadeIn
                delay={60}
                className="gn-analytics-dashboard__kpi-grid"
                data-analytics-funnel-kpis
              >
                <StatsCard
                  label={copy.fallback("analytics.funnel.entries", "Entries")}
                  value={activeResult?.totalEntries.toLocaleString() ?? "0"}
                  icon="IN"
                />
                <StatsCard
                  label={copy.fallback(
                    "analytics.dashboard.conversions",
                    "Conversions",
                  )}
                  value={activeResult?.totalConversions.toLocaleString() ?? "0"}
                  icon="OK"
                />
                <StatsCard
                  label={copy.fallback(
                    "analytics.dashboard.conversionRate",
                    "Conversion rate",
                  )}
                  value={formatPercent(activeResult?.overallConversionRate)}
                  icon="%"
                />
              </FadeIn>

              <div className="gn-analytics-funnel-page__grid">
                <FadeIn delay={120}>
                  <Card data-analytics-funnel-builder>
                    <CardHeader>
                      <CardTitle data-analytics-section-title>
                        {funnelName}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="gn-analytics-funnel-page__steps">
                      {stepRows.length === 0 ? (
                        <EmptyState
                          title={copy.fallback(
                            "analytics.funnel.noSteps",
                            "No funnel steps configured.",
                          )}
                        />
                      ) : (
                        stepRows.map((step) => (
                          <article
                            key={`${step.order}-${step.event}`}
                            className="gn-analytics-funnel-page__step"
                            data-analytics-funnel-step
                          >
                            <span className="gn-analytics-funnel-page__step-index">
                              {step.order}
                            </span>
                            <div>
                              <strong>{step.name}</strong>
                              <p className="gn-analytics-funnel-page__step-meta">
                                {step.event} - {step.propertyCount}{" "}
                                {copy.fallback(
                                  "analytics.funnel.propertyFilters",
                                  "property filters",
                                )}
                              </p>
                            </div>
                          </article>
                        ))
                      )}
                      <div className="gn-analytics-funnel-page__actions">
                        <Button
                          type="button"
                          variant="primary"
                          disabled={!canAnalyze || isLoading}
                          onClick={runAnalysis}
                        >
                          {copy.fallback(
                            "analytics.funnel.analyze",
                            "Analyze funnel",
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </FadeIn>

                <FadeIn delay={180}>
                  {isLoading ? (
                    <Card data-analytics-funnel-loading>
                      <CardContent className="gn-analytics-funnel-page__loading">
                        <Skeleton height={24} />
                        <Skeleton height={160} />
                      </CardContent>
                    </Card>
                  ) : activeResult ? (
                    <FunnelChart result={activeResult} />
                  ) : (
                    <Card data-analytics-funnel-empty>
                      <CardContent>
                        <EmptyState
                          title={copy.fallback(
                            "analytics.funnel.noAnalysis",
                            "No funnel analysis yet.",
                          )}
                        />
                      </CardContent>
                    </Card>
                  )}
                </FadeIn>
              </div>
            </>
          )}
        </div>
      </AnalyticsPageErrorBoundary>
    </>
  );
}
